"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAddToCart } from "@/hooks/useAddToCart";
import { trackSelectItem, trackViewItemList } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { toAnalyticsListItems } from "../listing/list-analytics";
import { ProductCard } from "../card/ProductCard";
import type { Product } from "@/stores/product-store";

/**
 * Serializable shape of a single carousel item — pre-resolved by the parent
 * server component so the client never has to talk to D1/R2 itself. Carries
 * every field the shared `<ProductCard />` needs so the carousel renders the
 * exact same card visual as the products grid (single source of truth).
 *
 * `href` is the canonical PDP URL used for card clicks and image zooms.
 */
export interface CarouselProduct {
  id: string;
  /** Descriptive PDP slug; falls back to id in the shared card URL builder. */
  slug?: string | null;
  title: string;
  description: string | null;
  pricePence: number | null;
  imageKey: string;
  imageUrl: string;
  imageUrlRaw?: string;
  inStock: number;
  deliveryTiming?: string;
  category: string;
  occasions: string | null;
  href: string;
  variants?: { id: string; label: string; pricePence: number | null }[];
}

export interface ProductCarouselClientProps {
  items: CarouselProduct[];
  /** Auto-scroll cadence in ms. Set to 0 to disable autoplay. */
  autoplayMs?: number;
  /** Section heading. */
  title: string;
  overline?: string;
  description?: string;
  viewAllHref?: string;
  /** Stable GA4 `item_list_id` naming this surface (see ProductCarousel). */
  listId?: string;
  /** Human list label. Defaults to the section `title`. */
  listName?: string;
}

const CARD_GAP_PX = 16; // matches `gap-4` in the row
const reducedMotionQuery = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(onChange: () => void) {
  const mediaQuery = window.matchMedia(reducedMotionQuery);
  mediaQuery.addEventListener?.("change", onChange);
  return () => mediaQuery.removeEventListener?.("change", onChange);
}

function getReducedMotionSnapshot() {
  return window.matchMedia(reducedMotionQuery).matches;
}

function getServerReducedMotionSnapshot() {
  return false;
}

export function ProductCarouselClient({
  items,
  autoplayMs = 5000,
  title,
  overline,
  description,
  viewAllHref,
  listId = "product-carousel",
  listName,
}: ProductCarouselClientProps) {
  const resolvedListName = listName ?? title;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    getServerReducedMotionSnapshot
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  // Cart write + GA4 add_to_cart in one call — see hooks/useAddToCart.
  const addToCart = useAddToCart();

  // Keep the prev/next button enabled state synced with scroll position.
  const updateButtonState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateButtonState();
    el.addEventListener("scroll", updateButtonState, { passive: true });
    window.addEventListener("resize", updateButtonState);
    return () => {
      el.removeEventListener("scroll", updateButtonState);
      window.removeEventListener("resize", updateButtonState);
    };
  }, [updateButtonState]);

  const cardStep = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const firstCard = el.querySelector<HTMLElement>("[data-carousel-card]");
    return firstCard ? firstCard.offsetWidth + CARD_GAP_PX : el.clientWidth;
  }, []);

  const scrollByCard = useCallback(
    (direction: 1 | -1) => {
      const el = scrollerRef.current;
      if (!el) return;
      const step = cardStep() * direction;
      const behavior: ScrollBehavior = reducedMotion ? "auto" : "smooth";
      const max = el.scrollWidth - el.clientWidth;
      const nearEnd = direction > 0 && el.scrollLeft >= max - 4;
      const nearStart = direction < 0 && el.scrollLeft <= 4;
      if (nearEnd) {
        el.scrollTo({ left: 0, behavior });
      } else if (nearStart) {
        el.scrollTo({ left: max, behavior });
      } else {
        el.scrollBy({ left: step, behavior });
      }
    },
    [cardStep, reducedMotion]
  );

  // Autoplay loop. Pauses on hover, focus, page-hidden, and reduced-motion.
  useEffect(() => {
    if (autoplayMs <= 0 || paused || reducedMotion) return;
    if (items.length <= 1) return;
    const interval = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      scrollByCard(1);
    }, autoplayMs);
    return () => clearInterval(interval);
  }, [autoplayMs, paused, reducedMotion, scrollByCard, items.length]);

  // `view_item_list` once the row is actually on screen. The carousel sits
  // mid-page on the home + landing pages, so firing on mount would report an
  // impression for a row most visitors never scroll to.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (items.length === 0) return;
    const el = scrollerRef.current;
    if (!el) return;
    let cancelled = false;
    const fire = () => {
      if (cancelled || reportedRef.current) return;
      reportedRef.current = true;
      trackViewItemList(listId, resolvedListName, toAnalyticsListItems(items));
    };
    if (typeof IntersectionObserver === "undefined") {
      fire();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          fire();
          io.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [items, listId, resolvedListName]);

  const router = useRouter();

  const handleAdd = useCallback(
    (item: CarouselProduct) => {
      if (item.pricePence == null || item.deliveryTiming === "enquireOnly") {
        toast.info("This piece is bespoke — please enquire for pricing.");
        return;
      }
      addToCart({
        productId: item.id,
        quantity: 1,
        priceSnapshot: item.pricePence,
        title: item.title,
        imageKey: item.imageKey,
        imageUrlRaw: item.imageUrlRaw,
        category: item.category,
      });
      toast.success(`${item.title} added to cart`);
    },
    [addToCart]
  );

  /** Map a serializable carousel item to the shape `<ProductCard />` expects. */
  const toProduct = useCallback(
    (item: CarouselProduct): Product => ({
      id: item.id,
      slug: item.slug,
      title: item.title,
      description: item.description,
      category: item.category,
      pricePence: item.pricePence,
      imageKey: item.imageKey,
      imageUrl: item.imageUrl,
      imageUrlRaw: item.imageUrlRaw,
      inStock: item.inStock,
      occasions: item.occasions,
      deliveryTiming: item.deliveryTiming,
      variants: item.variants,
    }),
    []
  );

  if (items.length === 0) return null;

  return (
    <section
      className="bg-background px-6 py-12 md:px-10 md:py-16 lg:px-16"
      aria-label={title}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl">
        {/* Header row.
            Mobile: heading stacked + "View all" link beneath the description.
            Chevrons are hidden on mobile because users swipe to scroll —
            keeping them at the top is a desktop ergonomic, not a mobile one.
            Desktop (sm+): heading on the left, chevrons + "View all" on the right. */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-4">
          <div className="min-w-0">
            {overline && (
              <p className="label-overline text-muted-foreground">{overline}</p>
            )}
            <h2 className="heading-section mt-2">{title}</h2>
            {description && (
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
                {description}
              </p>
            )}
            {viewAllHref && (
              <Link
                href={viewAllHref}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.1em] text-primary transition-colors hover:text-foreground sm:hidden"
              >
                View all
                <ArrowRight className="size-4" />
              </Link>
            )}
          </div>
          <div className="hidden shrink-0 items-center gap-2 sm:flex">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              disabled={!canScrollPrev}
              aria-label="Scroll to previous products"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition-all hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border disabled:hover:text-foreground sm:size-10 sm:min-h-0 sm:min-w-0"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              disabled={!canScrollNext}
              aria-label="Scroll to next products"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition-all hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border disabled:hover:text-foreground sm:size-10 sm:min-h-0 sm:min-w-0"
            >
              <ChevronRight className="size-5" />
            </button>
            {viewAllHref && (
              <Link
                href={viewAllHref}
                className="ml-2 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.1em] text-primary transition-colors hover:text-foreground"
              >
                View all
                <ArrowRight className="size-4" />
              </Link>
            )}
          </div>
        </div>

        {/* Scroller — constrained to the same max-w-7xl as the header.
            No bleed-to-viewport. Scrollbar hidden via inline + Tailwind. */}
        <div
          ref={scrollerRef}
          role="list"
          aria-roledescription="carousel"
          className={cn(
            "flex snap-x snap-mandatory gap-4 overflow-x-auto pb-1",
            "[scrollbar-width:none] [-ms-overflow-style:none]",
            "[&::-webkit-scrollbar]:hidden"
          )}
        >
          {items.map((item, index) => (
            <div
              key={item.id}
              data-carousel-card
              role="listitem"
              className="w-55 shrink-0 snap-start sm:w-60 md:w-65"
            >
              <ProductCard
                product={toProduct(item)}
                onClick={() => {
                  trackSelectItem(
                    listId,
                    resolvedListName,
                    toAnalyticsListItems([item], index)[0]!
                  );
                  router.push(item.href);
                }}
                onAddToCart={(_, e) => {
                  e.stopPropagation();
                  handleAdd(item);
                }}
                onZoom={() => router.push(item.href)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
